import { Box, Stack } from '@chakra-ui/react';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftCheckbox from '@/components/Atoms/FaceliftCheckbox';
import FaceliftText from '@/components/Atoms/FaceliftText';
import { useColorMode } from '@/components/ui/color-mode-utils';
import { useAcceptLicenseAgreement } from '@/hooks/service-hooks/auth';
import { useAuthStore } from '@/store/authStore';
import AgreementLayout from './AgreementLayout';

interface UserAgreementFormProps {
  onContinue: () => void;
}

const UserAgreementForm = ({ onContinue }: UserAgreementFormProps) => {
  const { colorMode } = useColorMode();
  const darkMode = colorMode === 'dark';
  const [isAgreed, setIsAgreed] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const user = useAuthStore((state) => state.user);
  const updateUserInformation = useAuthStore((state) => state.updateUserInformation);
  const queryClient = useQueryClient();

  const { mutate: acceptLicense, isLoading } = useAcceptLicenseAgreement(
    () => {
      const email = user?.userInformation.email;
      updateUserInformation({ isFirstLogin: false });

      if (email) {
        queryClient.setQueryData(['user', 'email', email], (currentUser: any) =>
          currentUser
            ? {
                ...currentUser,
                isFirstLogin: false,
              }
            : currentUser,
        );
        queryClient.invalidateQueries({ queryKey: ['user', 'email', email] });
      }

      toast.success('License agreement accepted successfully!');
      onContinue();
    },
    (error) => {
      const apiError = error as {
        response?: { data?: { message?: string } };
      };
      toast.error(
        apiError?.response?.data?.message ||
          'Failed to accept license agreement. Please try again.',
      );
    },
  );

  const handleContinue = () => {
    if (!isAgreed) return;

    const email = user?.userInformation.email;
    
    if (!email) {
      toast.error('User email not found. Please login again.');
      return;
    }

    acceptLicense({ email });
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const scrollHeight = target.scrollHeight;
    const scrollTop = target.scrollTop;
    const clientHeight = target.clientHeight;
    
    // Check if user has scrolled to the bottom (with a small threshold of 5px)
    if (scrollHeight - scrollTop - clientHeight < 5) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <Stack>
      <Stack bg={!darkMode ? 'white' : 'darkModeBgColor'} px={4} py={2} borderRadius="md">
        <FaceliftText fontSize={{ sm: 18, md: 21, lg: 28 }} fontWeight="bold">End User License Agreement (EULA)</FaceliftText>
        <Box mt={8} px={2} mb={2} border={'1px solid'} borderColor={!darkMode ? 'gray.300' : 'gray.600'} borderRadius={'lg'}>
          <AgreementLayout onScroll={handleScroll} />
        </Box>
      </Stack>

      <Stack mt={2}>
        <FaceliftCheckbox 
          size={'sm'} 
          label="I have read and agree to the End User License Agreement (EULA)" 
          checked={isAgreed} 
          onCheckedChange={(checked) => setIsAgreed(!!checked)}
          disabled={!hasScrolledToBottom}
        />
        <FaceliftButton
            mt={2}
            onClick={handleContinue}
            borderRadius="6px"
            size="md"
            transition="all 0.3s ease"
            _hover={{
              boxShadow: `0 0 10px 3px`,
              border: 'none',
              transform: 'scale(1.02)',
            }}
            letterSpacing={'widest'}
            disabled={!isAgreed || isLoading}
            loading={isLoading}
          >
           Accept and Continue
          </FaceliftButton>
      </Stack>
    </Stack>
  )
}

export default UserAgreementForm;
